import { useCallback, useSyncExternalStore } from 'react'
import { Settings } from '../../../types/state'
import { saveSettings as updateSettings } from '../../actions/settings'
import { DisplayUeopt, SettingsType } from '../../constants/enums'
import { SETTINGSSTATE } from '../../state'
import { pubSubSettings } from '../../state/pubSub'

const getSettings =
    <T extends keyof Settings>(type: T) =>
    () =>
        SETTINGSSTATE[type]

const getSettingSelector =
    <T extends keyof Settings, K extends keyof Settings[T]>(type: T, key: K) =>
    () =>
        SETTINGSSTATE[type][key]

const subSettings =
    <T extends keyof Settings>(type: T) =>
    (callback: () => void) =>
        pubSubSettings.on(type, callback)

export const useSettings = <T extends keyof Settings>(type: T) => {
    const settings = useSyncExternalStore(
        subSettings(type),
        getSettings(type),
        getSettings(type),
    )

    const saveSettings = useCallback(
        <K extends keyof Settings[T]>(key: K, value: Settings[T][K]) =>
            updateSettings(type, key, value),
        [type],
    )

    return [settings, saveSettings] as const
}

export const useSettingSelector = <
    T extends keyof Settings,
    K extends keyof Settings[T],
>(
    type: T,
    key: K,
): [Settings[T][K], (value: Settings[T][K]) => void] => {
    const setting = useSyncExternalStore(
        subSettings(type),
        getSettingSelector(type, key),
        getSettingSelector(type, key),
    )

    const saveSettings = useCallback(
        (value: Settings[T][K]) => updateSettings(type, key, value),
        [type, key],
    )

    return [setting, saveSettings]
}

/** display type a page uses until the user picks one; list every path of a route */
const UNCHOSEN_DISPLAY_BY_PAGE: Partial<Record<string, DisplayUeopt>> = {
    search: DisplayUeopt.preview,
    explore: DisplayUeopt.preview,
}

/** `display` for `page`, falling back to the page's default until `displayChosen` */
export const useDisplaySetting = (page?: string) => {
    const unchosenDefault =
        UNCHOSEN_DISPLAY_BY_PAGE[page] ?? DisplayUeopt.default
    const [display, saveDisplay] = useSettingSelector(
        SettingsType.DEFAULT,
        'display',
    )
    const [displayChosen] = useSettingSelector(
        SettingsType.DEFAULT,
        'displayChosen',
    )

    return [
        displayChosen ? display : unchosenDefault,
        saveDisplay,
        !!displayChosen,
    ] as const
}
